import { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Fiber Network Construction",
  "Coax/HFC Network Construction",
  "Fiber Splicing & Testing",
  "Network Upgrade",
  "Emergency Restoration",
  "Other"
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    project_location: "",
    project_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, project_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Your message has been sent successfully!");
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        project_location: "",
        project_type: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Contact Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Let's Start a Conversation
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Ready to discuss your next telecommunications project? 
              Reach out to our team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                Contact Information
              </h2>
              <div className="section-line mb-8"></div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a 
                      href="mailto:admin@cfcomm.us" 
                      className="text-[#00B4D8] hover:underline"
                      data-testid="contact-email-link"
                    >
                      admin@cfcomm.us
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Locations</h3>
                    <p className="text-slate-600 text-sm">
                    Ohio<br />
                    Available Nationwide
                  </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-slate-50 border-l-4 border-[#00B4D8]">
                <h3 className="font-semibold text-slate-900 mb-2">Quick Response</h3>
                <p className="text-slate-600 text-sm">
                  We typically respond to inquiries within 24 business hours.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="text-center py-12 bg-slate-50" data-testid="form-success">
                  <CheckCircle2 className="h-16 w-16 text-[#00B4D8] mx-auto mb-6" />
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                    Message Received!
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Thank you for reaching out. Our team will review your inquiry and get back to you soon.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#00B4D8] font-semibold hover:underline"
                    data-testid="send-another-button"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                        placeholder="Your full name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-slate-700 font-medium">
                        Company *
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                        placeholder="Your company name"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-slate-700 font-medium">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                        placeholder="you@company.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="project_location" className="text-slate-700 font-medium">
                        Project Location *
                      </Label>
                      <Input
                        id="project_location"
                        name="project_location"
                        value={formData.project_location}
                        onChange={handleChange}
                        required
                        className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                        placeholder="City, State"
                        data-testid="input-project-location"
                      />
                    </div>
                    <div>
                      <Label htmlFor="project_type" className="text-slate-700 font-medium">
                        Project Type *
                      </Label>
                      <Select onValueChange={handleSelectChange} value={formData.project_type} required>
                        <SelectTrigger 
                          className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                          data-testid="select-project-type"
                        >
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type} className="cursor-pointer">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      placeholder="Tell us about your project requirements..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Request Project Discussion
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
